import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { supabase } from '@/integrations/supabase/client';
import { useServiceLogs } from '@/hooks/useServiceLogs';
import { useRosters } from '@/hooks/useRosters';
import '../styles/print.css';
import { normalizeStatus, displayStatus, ClientStatus, isClientOnPass } from '@/utils/statusTransitions';
import { useClientPhotos } from '@/hooks/useClientPhotos';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { useClients } from '@/hooks/useClients';
import { useAlerts } from '@/hooks/useAlerts';
import { usePassExpirationCheck } from '@/hooks/usePassExpirationCheck';
import { Client, ServiceRecord } from '../types/shelter';
import NewClientForm from './NewClientForm';
import ClientProfile from './ClientProfile';
import { GlobalSearchCommand } from './GlobalSearchCommand';
import { format } from 'date-fns';
import { cn, formatCheckTime } from '@/lib/utils';
import johnPhoto from '@/assets/client-john.jpg';
import michaelPhoto from '@/assets/client-michael.jpg';
import robertPhoto from '@/assets/client-robert.jpg';
import davidPhoto from '@/assets/client-david.jpg';
import carlosPhoto from '@/assets/client-carlos.jpg';
import anthonyPhoto from '@/assets/client-anthony.jpg';
import marcusPhoto from '@/assets/client-marcus.jpg';
import stevenPhoto from '@/assets/client-steven.jpg';
import { User, Users, UserCheck, UserX, Clock, Building2, LogOut, Camera, AlertTriangle, CheckCircle2, Activity, UserPlus, Play, HelpCircle, MessageSquare, BookOpen, Video, Phone, Mail, MapPin, ChevronDown, ChevronRight, X, FileText, Send, Tag, Star, Eye, Search, ArrowUpDown, ArrowUp, ArrowDown, Menu, Home, Settings, BarChart, Bot, Calendar as CalendarIcon, MoreHorizontal, Plus, Download, Bell, ClipboardList, StickyNote, ClipboardPlus, ClipboardCheck } from 'lucide-react';
import UserGuide from './UserGuide';
import { MyProfileDialog } from './profile/MyProfileDialog';
import { BedCountLauncher } from '@/components/bedcount/BedCountLauncher';
import ShiftReport from './staff/ShiftReport';
import { logActivity } from '@/hooks/useActivityLog';
import { isWithinCurrentOperationalDay } from '@/utils/operationalDay';
const StaffDashboard = () => {
  const {
    user,
    logout,
    userSites
  } = useAuth();
  const {
    clients,
    isLoading: clientsLoading,
    updateClient,
    createClient,
    changeStatus,
    isChangingStatus
  } = useClients();
  const {
    alerts: dbAlerts = [],
    isLoading: alertsLoading,
    createAlert,
    updateAlert
  } = useAlerts();
  usePassExpirationCheck();
  const { rosters } = useRosters();

  // Build set of rostered client IDs from today's roster only
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const todaysRosters = useMemo(() => {
    return rosters.filter((r: any) => r.roster_date === todayStr);
  }, [rosters, todayStr]);

  const rosteredClientIds = useMemo(() => {
    const ids = new Set<string>();
    todaysRosters.forEach((roster: any) => {
      (roster.roster_entries || []).forEach((entry: any) => {
        const clientId = entry.client_id || entry.client?.id;
        if (clientId) ids.add(clientId);
      });
    });
    return ids;
  }, [todaysRosters]);

  // Clients for Daily Census: use today's roster if one exists, otherwise show all clients.
  // Unbedded clients only stay on the roster/census during the same operational day
  // (rolls over at 2 AM); after that they remain in the Clients list but drop off the census.
  const rosteredClients = useMemo(() => {
    const base = todaysRosters.length === 0
      ? clients
      : clients.filter((c) => rosteredClientIds.has(c.id));
    return base.filter((c: any) => {
      if (c.status !== 'unbedded') return true;
      return isWithinCurrentOperationalDay(c.unbedded_at ?? c.unbeddedAt);
    });
  }, [clients, rosteredClientIds, todaysRosters]);

  const alerts = dbAlerts;
  const { createServiceLog } = useServiceLogs();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null);
  const [showMyProfile, setShowMyProfile] = useState(false);

  // Virtualized table ref
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Quick Action state
  const [servicePopoverClient, setServicePopoverClient] = useState<string | null>(null);
  const [notePopoverClient, setNotePopoverClient] = useState<string | null>(null);
  const [quickNoteText, setQuickNoteText] = useState('');
  const [serviceQuantity, setServiceQuantity] = useState(1);

  // Initialize daily services on component mount
  useEffect(() => {
    initializeDailyServices();
  }, []);

  // Sidebar navigation
  const [activeSection, setActiveSection] = useState('census');

  // Alert management
  const [newAlertTitle, setNewAlertTitle] = useState('');
  const [newAlertDescription, setNewAlertDescription] = useState('');
  const [newAlertType, setNewAlertType] = useState<'critical' | 'warning' | 'info'>('info');
  const [newAlertCategory, setNewAlertCategory] = useState<'staff' | 'service' | 'building'>('staff');
  const [selectedClientForAlert, setSelectedClientForAlert] = useState<string>('');
  const [alertFilter, setAlertFilter] = useState<'all' | 'client' | 'staff' | 'system'>('all');

  // Chat interface
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{
    id: string;
    type: 'user' | 'assistant';
    content: string;
    timestamp: Date;
  }>>([]);

  // Help data
  const helpTopics = [{
    id: 1,
    question: 'How do I check in a new client?',
    answer: 'Use the facial recognition scanner or manually search for the client in the system.'
  }, {
    id: 2,
    question: 'What should I do in case of an emergency?',
    answer: 'Follow the emergency protocols outlined in the safety manual and notify supervisors immediately.'
  }, {
    id: 3,
    question: 'How do I access client medical information?',
    answer: 'Medical information is available in the client profile under the Medical tab.'
  }, {
    id: 4,
    question: 'Who should I contact for maintenance issues?',
    answer: 'Report maintenance issues through the alert system or contact facilities management directly.'
  }];

  // Photo resolution shared across all surfaces
  const { getClientPhoto } = useClientPhotos(clients);
  const [isScanning, setIsScanning] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isNewClientFormOpen, setIsNewClientFormOpen] = useState(false);
  const [isClientProfileOpen, setIsClientProfileOpen] = useState(false);
  const [selectedProfileClient, setSelectedProfileClient] = useState<Client | null>(null);
  const [sortField, setSortField] = useState<'name' | 'room' | 'status' | 'checkInTime' | 'age'>('room');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCheckoutDialogOpen, setIsCheckoutDialogOpen] = useState(false);
  const [checkoutClient, setCheckoutClient] = useState<Client | null>(null);
  const [isCheckinDialogOpen, setIsCheckinDialogOpen] = useState(false);
  const [checkinClient, setCheckinClient] = useState<Client | null>(null);
  const [viewServicesClient, setViewServicesClient] = useState<Client | null>(null);
  const [isViewServicesOpen, setIsViewServicesOpen] = useState(false);
  const [quickAddNoteTitle, setQuickAddNoteTitle] = useState('');
  const [quickAddNoteContent, setQuickAddNoteContent] = useState('');
  const [quickAddService, setQuickAddService] = useState<{ type: 'metro' | 'laundry' | 'meal' | 'juice' | 'breakfast' | 'lunch' | 'dinner' | 'milk'; label: string; icon: string } | null>(null);
  const [quickAddQuantity, setQuickAddQuantity] = useState(1);
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const isMobile = useIsMobile();
  const checkedInCount = rosteredClients.filter((c) => c.status === 'checked-in').length;
  const totalClients = rosteredClients.length;
  const canSignOffBedCount = ['super_admin', 'program_director', 'manager', 'supervisor'].includes(user?.role ?? '');
  const handleFacialRecognition = () => {
    setIsScanning(true);
    setTimeout(() => {
      const availableClient = clients.find((c) => c.status === 'checked-out');
      if (availableClient) {
        setSelectedClient(availableClient);
        handleCheckIn(availableClient.id);
      }
      setIsScanning(false);
    }, 3000);
  };
  const handleCheckIn = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      setCheckinClient(client);
      setIsCheckinDialogOpen(true);
    }
  };
  const confirmCheckIn = () => {
    if (checkinClient) {
      if (!checkinClient.room_id) {
        toast({ title: "Room Required", description: "Assign a room to this client before checking in.", variant: "destructive" });
        setIsCheckinDialogOpen(false);
        setCheckinClient(null);
        return;
      }
      changeStatus({
        id: checkinClient.id,
        newStatus: 'checked-in'
      });
    }
    setIsCheckinDialogOpen(false);
    setCheckinClient(null);
  };
  const handleCheckOut = (clientId: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      setCheckoutClient(client);
      setIsCheckoutDialogOpen(true);
    }
  };
  const confirmCheckOut = () => {
    if (checkoutClient) {
      changeStatus({
        id: checkoutClient.id,
        newStatus: 'checked-out'
      });
    }
    setIsCheckoutDialogOpen(false);
    setCheckoutClient(null);
  };
  const getStatusColor = (status: Client['status']) => {
    const s = normalizeStatus(status);
    switch (s) {
      case 'checked-in':
        return 'bg-[hsl(var(--status-checked-in))] text-[hsl(var(--status-checked-in-foreground))]';
      case 'checked-out':
        return 'bg-[hsl(var(--status-checked-out))] text-[hsl(var(--status-checked-out-foreground))]';
      case 'not_present':
        return 'bg-destructive/20 text-destructive';
      case 'reserved':
        return 'bg-primary/20 text-primary';
      case 'waitlist':
        return 'bg-accent text-accent-foreground';
      case 'unbedded':
        return 'bg-warning text-warning-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };
  const getStatusIcon = (status: Client['status']) => {
    const s = normalizeStatus(status);
    switch (s) {
      case 'checked-in':
        return <CheckCircle2 className="h-3 w-3" />;
      case 'checked-out':
        return <UserX className="h-3 w-3" />;
      case 'not_present':
        return <X className="h-3 w-3" />;
      case 'reserved':
        return <Clock className="h-3 w-3" />;
      case 'waitlist':
        return <Clock className="h-3 w-3" />;
      case 'unbedded':
        return <AlertTriangle className="h-3 w-3" />;
      default:
        return null;
    }
  };
  const handleNewClient = (newClient: Omit<Client, 'id'>) => {
    createClient(newClient);
  };
  const handleSelectClient = (client: Client) => {
    setSelectedProfileClient(client);
    setIsClientProfileOpen(true);
  };
  const handleViewServices = (client: Client) => {
    setViewServicesClient(client);
    setIsViewServicesOpen(true);
    setQuickAddNoteTitle('');
    setQuickAddNoteContent('');
    setQuickAddService(null);
    setQuickAddQuantity(1);
  };
  const handleUpdateClient = (updatedClient: Client) => {
    updateClient(updatedClient);
    setSelectedProfileClient(updatedClient);
  };
  const handleAddServiceRecord = (clientId: string, service: any) => {
    console.log('Service added for client:', clientId, service);
  };

  const handleQuickService = async (clientId: string, serviceType: 'metro' | 'laundry' | 'meal' | 'juice' | 'breakfast' | 'lunch' | 'dinner' | 'milk') => {
    createServiceLog({ clientId, serviceType, quantity: serviceQuantity });
    setServicePopoverClient(null);
    setServiceQuantity(1);
    await logActivity({
      actionType: 'add_service',
      actionDetails: { client_id: clientId, service_type: serviceType, quantity: serviceQuantity }
    });
  };

  const handleQuickNote = async (client: Client) => {
    if (!quickNoteText.trim()) return;
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        toast({ variant: 'destructive', title: 'Error', description: 'Not authenticated' });
        return;
      }
      const { error } = await supabase.from('notes').insert({
        client_id: client.id,
        site_id: client.site_id || null,
        title: 'Quick Note',
        content: quickNoteText,
        created_by: authUser.id
      });
      if (error) throw error;
      toast({ title: 'Note Added', description: `Quick note saved for ${client.name}` });
      await logActivity({
        actionType: 'create_note',
        actionDetails: { client_id: client.id, client_name: client.name, note_type: 'quick_note' },
        siteId: client.site_id
      });
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message || 'Failed to save note' });
    }
    setNotePopoverClient(null);
    setQuickNoteText('');
  };
  const handleClearAlert = (clientId: string, alertText: string, staffName: string) => {
    const client = clients.find((c) => c.id === clientId);
    if (client && client.alerts) {
      updateClient({
        id: clientId,
        alerts: client.alerts.filter((alert) => alert !== alertText)
      });
    }

    // Update the selected profile client if it's the same one
    if (selectedProfileClient?.id === clientId) {
      setSelectedProfileClient((prev) => {
        if (prev && prev.alerts) {
          return {
            ...prev,
            alerts: prev.alerts.filter((alert) => alert !== alertText)
          };
        }
        return prev;
      });
    }
  };

  // Get available rooms list
  const availableRooms = ['101A', '101B', '102A', '102B', '103A', '103B', '201A', '201B', '202A', '202B', '203A', '203B', '204A', '204B', '301A', '301B', '302A', '302B'];

  // Service management functions
  const updateDailyService = (clientId: string, serviceType: keyof Omit<ServiceRecord, 'date'>, quantity: number) => {
    const client = clients.find((c) => c.id === clientId);
    if (client && client.services) {
      const newDaily = {
        ...client.services.daily,
        [serviceType]: quantity
      };
      const currentDailyValue = client.services.daily[serviceType] as number;
      const currentTotalValue = client.services.total[serviceType] as number;
      const totalChange = quantity - currentDailyValue;
      const newTotal = {
        ...client.services.total,
        [serviceType]: Math.max(0, currentTotalValue + totalChange)
      };
      updateClient({
        id: clientId,
        services: {
          daily: newDaily,
          total: newTotal
        }
      });
    }
  };

  // Initialize daily services if needed (reset to zero each day)
  const initializeDailyServices = () => {
    const today = new Date().toISOString().split('T')[0];
    clients.forEach((client) => {
      if (client.services && client.services.daily.date !== today) {
        updateClient({
          id: client.id,
          services: {
            ...client.services,
            daily: {
              date: today,
              metro: 0,
              laundry: 0,
              meal: 0,
              juice: 0
            }
          }
        });
      }
    });
  };

  // Sorting and filtering functions for census
  const handleSort = (field: 'name' | 'room' | 'status' | 'checkInTime' | 'age') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const getSortedClients = () => {
    return rosteredClients.sort((a, b) => {
      let aValue: string | number | Date;
      let bValue: string | number | Date;
      switch (sortField) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'room':
          aValue = a.room || 'zzz';
          bValue = b.room || 'zzz';
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'checkInTime':
          aValue = a.checkInTime ? new Date(a.checkInTime) : new Date(0);
          bValue = b.checkInTime ? new Date(b.checkInTime) : new Date(0);
          break;
        case 'age':
          aValue = a.age;
          bValue = b.age;
          break;
        default:
          aValue = a.name || '';
          bValue = b.name || '';
      }
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  };

  // Handle chat input
  const handleChatSubmit = () => {
    if (!chatInput.trim()) return;
    const userMessage = {
      id: `msg-${Date.now()}`,
      type: 'user' as const,
      content: chatInput,
      timestamp: new Date()
    };
    setChatMessages((prev) => [...prev, userMessage]);

    // Simulate AI response based on input
    setTimeout(() => {
      const response = generateAIResponse(chatInput);
      const aiMessage = {
        id: `msg-${Date.now()}-ai`,
        type: 'assistant' as const,
        content: response,
        timestamp: new Date()
      };
      setChatMessages((prev) => [...prev, aiMessage]);
    }, 1000);
    setChatInput('');
  };
  const generateAIResponse = (input: string): string => {
    const lowerInput = input.toLowerCase();

    // Client search
    if (lowerInput.includes('client') || lowerInput.includes('resident') || lowerInput.includes('room')) {
      const matches = clients.filter((c) => c.name?.toLowerCase().includes(lowerInput) || c.room && lowerInput.includes(c.room.toLowerCase()));
      if (matches.length > 0) {
        return `I found ${matches.length} client(s): ${matches.map((c) => `${c.name} (Room ${c.room})`).join(', ')}. Would you like to view their profile?`;
      }
      return "I can help you search for clients by name or room number. What specific client are you looking for?";
    }


    // Help topics
    if (lowerInput.includes('help') || lowerInput.includes('how') || lowerInput.includes('?')) {
      const matches = helpTopics.filter((h) => h.question.toLowerCase().includes(lowerInput) || h.answer.toLowerCase().includes(lowerInput));
      if (matches.length > 0) {
        return matches[0].answer;
      }
      return "I can help with: client check-in procedures, emergency protocols, accessing medical information, maintenance issues, and more. What do you need help with?";
    }

    // Census and capacity
    if (lowerInput.includes('census') || lowerInput.includes('capacity') || lowerInput.includes('occupancy')) {
      return `Current occupancy: ${checkedInCount}/${totalClients} clients checked in. ${totalClients - checkedInCount} clients currently checked out.`;
    }

    // Default response
    return "I can help you with client information, training videos, help topics, capacity status, and general shelter operations. What do you need assistance with?";
  };
  // WM v1: Simplified staff sidebar
  const menuItems = [{
    id: 'census',
    label: 'Census View',
    icon: BarChart
  }, {
    id: 'alerts',
    label: 'Updates',
    icon: AlertTriangle
  }, {
    id: 'settings',
    label: 'Settings',
    icon: Settings
  }];
  return <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b sticky top-0 z-10 bg-background">
        <div className="flex min-h-14 items-start px-4 py-2 lg:px-6">
          {/* Left: Date */}
          <div className="flex flex-col items-start gap-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">{format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <CalendarIcon className="h-3.5 w-3.5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={selectedDate} onSelect={(date) => date && setSelectedDate(date)} initialFocus className={cn("p-3 pointer-events-auto")} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex w-full flex-col gap-1.5">
              <Button variant="outline" size="sm" className="h-8 justify-start" onClick={() => setActiveSection('shift-report')}>
                <FileText className="h-4 w-4" />
                Shift Report
              </Button>
              <Button variant="outline" size="sm" className="h-8 justify-start" onClick={() => setActiveSection('bed-count')}>
                <ClipboardCheck className="h-4 w-4" />
                Bed Count
              </Button>
            </div>
          </div>
          {/* Center: Search */}
          <div className="flex-1 flex items-center justify-center">
            <GlobalSearchCommand />
          </div>
          {/* Right: Actions */}
          <div className="flex items-center gap-2">
            {/* Alerts Button */}
            <Button variant="ghost" className="flex items-center gap-2 px-2" onClick={() => setActiveSection('alerts')}>
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center relative">
                <Bell className="h-4 w-4 text-primary" />
                {alerts.length > 0 && <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px] flex items-center justify-center font-bold">
                    {alerts.length}
                  </span>}
              </div>
              
            </Button>

            {/* Settings Button */}
            <Button variant="ghost" className="flex items-center gap-2 px-2" onClick={() => setActiveSection('settings')}>
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Settings className="h-4 w-4 text-primary" />
              </div>
              
            </Button>
            
            {/* User Avatar & Profile Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-4 w-4 text-primary" />
                  </div>
                  <span className="hidden md:inline-block text-sm font-medium max-w-[120px] truncate">
                    {user?.name || 'Staff'}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover border shadow-md">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.name || 'Staff'}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role || 'Staff'}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowMyProfile(true)}>
                  <User className="mr-2 h-4 w-4" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveSection('settings')}>
                  <Bell className="mr-2 h-4 w-4" />
                  Notification Settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setActiveSection('user-guide')}>
                  <HelpCircle className="mr-2 h-4 w-4" />
                  User Manual
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="flex-1 p-6">
          {/* Census Section - Main Page */}
          {activeSection === 'census' && <div className="space-y-6">
                {/* Main Header with Search */}
                <div className="text-center space-y-8">
                <div className="space-y-3 relative">
                    <h1 className="text-5xl text-foreground tracking-tight font-medium font-serif">Daily Census</h1>
                  </div>


                  {/* Chat Messages */}
                  {chatMessages.length > 0 && <Card className="max-w-2xl mx-auto">
                      <CardContent className="pt-6">
                        <div className="space-y-4 max-h-60 overflow-y-auto">
                          {chatMessages.slice(-3).map((message) => <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-xs p-3 rounded-lg ${message.type === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                                <p className="text-sm">{message.content}</p>
                                <p className="text-xs opacity-70 mt-1">
                                  {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                                </p>
                              </div>
                            </div>)}
                        </div>
                      </CardContent>
                    </Card>}
                </div>

                
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-4 md:gap-6">
                  {[
            { key: 'checkedIn', label: 'Currently Checked In', count: checkedInCount, color: 'hsl(152, 45%, 42%)', clients: rosteredClients.filter((c) => c.status === 'checked-in') },
            { key: 'checkedOut', label: 'Checked Out', count: rosteredClients.filter((c) => c.status === 'checked-out').length, color: undefined, colorClass: 'text-gators-blue', clients: rosteredClients.filter((c) => c.status === 'checked-out') },
            { key: 'notPresent', label: 'Not Present', count: rosteredClients.filter((c) => ['not_present', 'reserved', 'waitlist'].includes(c.status as string)).length, color: undefined, colorClass: 'text-destructive', clients: rosteredClients.filter((c) => ['not_present', 'reserved', 'waitlist'].includes(c.status as string)) },
            { key: 'pass', label: 'Pass', sublabel: '(within Checked In)', count: rosteredClients.filter((c) => isClientOnPass(c)).length, color: undefined, colorClass: 'text-warning', clients: rosteredClients.filter((c) => isClientOnPass(c)) },
            { key: 'total', label: 'Total Clients', count: totalClients, color: undefined, colorClass: 'text-gators-orange', clients: rosteredClients }].
            map((stat: any) =>
            <Popover key={stat.key}>
                      <PopoverTrigger asChild>
                        <Card className="cursor-pointer hover:ring-2 ring-primary/20 transition-all rounded-none">
                          <CardContent className="p-3 sm:p-4 md:p-6">
                            <div className="text-center">
                              <p className={`text-2xl sm:text-3xl font-bold tabular-nums ${stat.colorClass || ''}`} style={stat.color ? { color: stat.color } : undefined}>{stat.count}</p>
                              <p className="text-[11px] sm:text-sm md:text-base text-muted-foreground uppercase sm:normal-case tracking-wide sm:tracking-normal leading-tight mt-0.5">{stat.label}</p>
                              {stat.sublabel && (
                                <p className="hidden sm:block text-xs text-muted-foreground/70 mt-0.5">{stat.sublabel}</p>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </PopoverTrigger>
                      <PopoverContent className="w-64 max-h-64 overflow-y-auto">
                        <p className="font-semibold mb-2">{stat.label} ({stat.count})</p>
                        {stat.clients.length === 0 ?
                <p className="text-sm text-muted-foreground">No clients</p> :
                stat.clients.map((c: any) =>
                <button key={c.id} onClick={() => handleSelectClient(c)} className="block w-full text-left hover:underline p-1 text-sm">
                            {c.name}
                            {stat.key === 'notPresent' && c.status !== 'not_present' && (
                              <span className="text-xs text-muted-foreground ml-1">· {c.status}</span>
                            )}
                          </button>
                )}
                      </PopoverContent>
                    </Popover>
            )}
                </div>


                {/* Client List Table */}
                <Card>
                  <CardContent className="pt-6">
                    {/* Desktop Table View */}
                    <div className="rounded-md border hidden lg:block">
                      <div ref={tableContainerRef} className="max-h-[600px] overflow-auto">
                      <table className="w-full min-w-[980px] table-fixed">
                        <colgroup>
                          <col className="w-[26%]" />
                          <col className="w-[10%]" />
                          <col className="w-[28%]" />
                          <col className="w-[270px]" />
                          <col className="w-[190px]" />
                        </colgroup>
                         <thead>
                           <tr>
                              <th className="sticky top-0 z-20 border-b bg-background p-4 text-left text-base font-medium shadow-sm cursor-pointer" onClick={() => handleSort('name')}>
                                <div className="flex items-center gap-2">
                                  Name
                                  <ArrowUpDown className="h-4 w-4" />
                                </div>
                              </th>
                               <th className="sticky top-0 z-20 border-b bg-background p-4 text-left text-base font-medium shadow-sm cursor-pointer" onClick={() => handleSort('room')}>
                                <div className="flex items-center gap-2">
                                  Room
                                  <ArrowUpDown className="h-4 w-4" />
                                </div>
                              </th>
                               <th className="sticky top-0 z-20 border-b bg-background p-4 text-left text-base font-medium shadow-sm">Alerts</th>
                                <th className="sticky top-0 z-20 border-b bg-background p-4 text-left text-base font-medium shadow-sm print:hidden whitespace-nowrap">Actions</th>
                               <th className="sticky top-0 z-20 border-b bg-background p-4 text-left text-base font-medium shadow-sm cursor-pointer" onClick={() => handleSort('checkInTime')}>
                                <div className="flex items-center gap-2 whitespace-nowrap">
                                  Last Activity
                                  <ArrowUpDown className="h-4 w-4" />
                                </div>
                              </th>
                           </tr>
                         </thead>
                        <tbody>
                           {getSortedClients().map((client) => <tr key={client.id} className="border-b hover:bg-secondary">
                               <td className="p-4">
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-10 w-10 print:hidden">
                                      <AvatarImage src={getClientPhoto(client.id)} alt={client.name} />
                                      <AvatarFallback className="bg-muted">
                                        <User className="h-1/2 w-1/2 text-muted-foreground" />
                                      </AvatarFallback>
                                    </Avatar>
                                     <div>
                         <button type="button" className="hover:underline underline-offset-2 focus:outline-none cursor-pointer print-show text-sidebar-primary font-normal text-base font-serif text-left shadow-none" onClick={() => handleSelectClient(client)}>
                                           {client.name}
                                         </button>
                                    </div>
                                  </div>
                               </td>
                               <td className="p-4">
                                 <span className="font-mono text-base">{client.room || 'N/A'}</span>
                               </td>
                                  <td className="p-4">
                                    {client.alerts && client.alerts.length > 0 ? <div className="flex flex-wrap gap-2">
                                        {client.alerts.map((alert, idx) => {
                            const isReminder = alert.toLowerCase().includes('reminder') || alert.toLowerCase().includes('appointment') || alert.toLowerCase().includes('follow up') || alert.toLowerCase().includes('ilp');
                            return <Popover key={idx}>
                                              <PopoverTrigger asChild>
                                                <span className="text-gators-orange hover:text-gators-orange/80 underline cursor-pointer text-base font-sans">
                                                  {alert}
                                                </span>
                                              </PopoverTrigger>
                                             <PopoverContent className="w-auto p-3" side="top">
                                               <div className="space-y-2">
                                                  <p className="text-xs text-muted-foreground">
                                                    {client.lastActivity && !isNaN(new Date(client.lastActivity).getTime()) ? format(new Date(client.lastActivity), 'MMM d, yyyy h:mm a') : 'No timestamp'}
                                                  </p>
{isReminder && <Button size="sm" className="w-full h-7 text-xs" onClick={async () => {
                                    // Remove alert string from client
                                    const updatedAlerts = client.alerts?.filter((a) => a !== alert) || [];
                                    await updateClient({ ...client, alerts: updatedAlerts });

                                    // Resolve the matching DB alert
                                    const { data: { user: currentUser } } = await supabase.auth.getUser();
                                    await supabase.from('alerts').update({ status: 'resolved', acknowledged_by: currentUser?.id || null }).eq('client_id', client.id).eq('title', alert).eq('status', 'active');

                                    await logActivity({
                                      actionType: 'acknowledge_reminder',
                                      actionDetails: {
                                        client_id: client.id,
                                        client_name: client.name,
                                        reminder_text: alert,
                                        timestamp: new Date().toISOString()
                                      },
                                      siteId: client.site_id
                                    });

                                    toast({
                                      title: "Reminder Acknowledged",
                                      description: `Alert resolved and logged`
                                    });
                                  }}>
                                                     Acknowledge
                                                   </Button>}
                                               </div>
                                             </PopoverContent>
                                           </Popover>;
                          })}
                                     </div> : <span className="text-base text-muted-foreground">None</span>}
                                 </td>
                                <td className="p-4 print:hidden">
                                  <div className="flex items-center gap-2 whitespace-nowrap">
                                    <Button variant="outline" size="sm" onClick={() => handleViewServices(client)} className="min-h-[44px] min-w-[112px] shadow-lg">
                      <ClipboardPlus className="h-3 w-3 mr-1" />
                                       Services
                                    </Button>
                                    {normalizeStatus(client.status) === 'checked-in' ?
                          <Button variant="default" size="sm" onClick={() => handleCheckOut(client.id)} className="min-h-[44px] min-w-[120px] rounded-none text-warning-foreground bg-sidebar-ring font-mono font-semibold shadow-lg">
                                        <UserX className="h-3 w-3 mr-1" />
                                        Check Out
                                      </Button> :

                          <Button variant="default" size="sm" onClick={() => handleCheckIn(client.id)} className="min-h-[44px] min-w-[120px] rounded-none text-success-foreground text-center font-mono font-semibold bg-success hover:bg-success/90">
                                        <UserCheck className="h-3 w-3 mr-1" />
                                        Check In
                                      </Button>
                          }
                                 </div>
                               </td>
                                     <td className="p-4">
                                        <div className="text-sm">
                                          {(() => {
                            const checkIn = client.checkInTime ? new Date(client.checkInTime) : null;
                            const checkOut = client.checkOutTime ? new Date(client.checkOutTime) : null;
                            let lastType = '';
                            let lastTime: Date | null = null;
                            if (checkIn && checkOut) {
                              if (checkIn >= checkOut) {lastType = 'Check In';lastTime = checkIn;} else
                              {lastType = 'Check Out';lastTime = checkOut;}
                            } else if (checkIn) {lastType = 'Check In';lastTime = checkIn;} else
                            if (checkOut) {lastType = 'Check Out';lastTime = checkOut;} else
                            if (client.lastActivity) {lastTime = new Date(client.lastActivity);}

                            return lastTime ?
                            <div>
                                                {lastType && <p className="text-xs font-medium text-foreground">{lastType}</p>}
                                                <p className="text-muted-foreground font-mono">
                                                  {format(lastTime, 'MMM d, h:mm a')}
                                                </p>
                                              </div> :
                            <p className="text-muted-foreground font-mono">No recent activity</p>;
                          })()}
                                        </div>
                                       </td>
                             </tr>)}
                        </tbody>
                      </table>
                      </div>
                    </div>
                    <div className="lg:hidden space-y-3">
                      {getSortedClients().map((client) => {
                  const checkIn = client.checkInTime ? new Date(client.checkInTime) : null;
                  const checkOut = client.checkOutTime ? new Date(client.checkOutTime) : null;
                  let lastType = '';
                  let lastTime: Date | null = null;
                  if (checkIn && checkOut) {
                    if (checkIn >= checkOut) {lastType = 'Check In';lastTime = checkIn;} else
                    {lastType = 'Check Out';lastTime = checkOut;}
                  } else if (checkIn) {lastType = 'Check In';lastTime = checkIn;} else
                  if (checkOut) {lastType = 'Check Out';lastTime = checkOut;} else
                  if (client.lastActivity) {lastTime = new Date(client.lastActivity);}

                  return (
                    <Card key={client.id} className="overflow-hidden">
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3 mb-3">
                                <Avatar className="h-12 w-12 flex-shrink-0">
                                  <AvatarImage src={getClientPhoto(client.id)} alt={client.name} />
                                  <AvatarFallback className="bg-muted">
                                    <User className="h-1/2 w-1/2 text-muted-foreground" />
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <button type="button" className="hover:underline underline-offset-2 focus:outline-none cursor-pointer text-sidebar-primary font-medium text-base font-serif text-left" onClick={() => handleSelectClient(client)}>
                                    {client.name}
                                  </button>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className="font-mono text-sm text-muted-foreground">Room {client.room || 'N/A'}</span>
                                    <Badge className={cn("text-xs", getStatusColor(client.status))}>
                                      {getStatusIcon(client.status)}
                                      <span className="ml-1 capitalize">{client.status.replace('-', ' ')}</span>
                                    </Badge>
                                  </div>
                                </div>
                              </div>

                               <div className="grid grid-cols-1 gap-2 text-sm mb-3">
                                <div>
                                  <p className="text-muted-foreground text-xs">Last Activity</p>
                                  {lastTime ?
                            <div>
                                      {lastType && <span className="text-xs font-medium text-foreground">{lastType} · </span>}
                                      <span className="font-mono text-xs">{format(lastTime, 'MMM d, h:mm a')}</span>
                                    </div> :
                            <span className="font-mono text-xs text-muted-foreground">No recent activity</span>}
                                </div>
                              </div>

                              {client.alerts && client.alerts.length > 0 &&
                        <div className="flex flex-wrap gap-1 mb-3">
                                  {client.alerts.map((alert, idx) =>
                          <Badge key={idx} variant="outline" className="text-xs text-gators-orange border-gators-orange/30">
                                      {alert}
                                    </Badge>
                          )}
                                </div>
                        }

                              <div className="flex items-center gap-2 print:hidden">
                                <Button variant="outline" size="sm" onClick={() => handleViewServices(client)} className="flex-1 min-h-[44px]">
                                  <ClipboardPlus className="h-4 w-4 mr-1" />
                                  Services
                                </Button>
                                {normalizeStatus(client.status) === 'checked-in' ?
                          <Button variant="default" size="sm" onClick={() => handleCheckOut(client.id)} className="flex-1 min-h-[44px] text-warning-foreground bg-sidebar-ring font-mono font-semibold">
                                    <UserX className="h-4 w-4 mr-1" />
                                    Check Out
                                  </Button> :

                          <Button variant="default" size="sm" onClick={() => handleCheckIn(client.id)} className="flex-1 min-h-[44px] text-success-foreground font-mono font-semibold bg-success hover:bg-success/90">
                                    <UserCheck className="h-4 w-4 mr-1" />
                                    Check In
                                  </Button>
                          }
                              </div>
                            </CardContent>
                          </Card>);

                })}
                    </div>
                  </CardContent>
                </Card>
              </div>}

          {activeSection === 'shift-report' && <ShiftReport onBack={() => setActiveSection('census')} />}

          {activeSection === 'bed-count' && <div className="space-y-4">
                <Button variant="ghost" onClick={() => setActiveSection('census')} className="mb-2">
                  <Home className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
                {userSites?.[0] ? (
                  <BedCountLauncher
                    siteId={userSites[0]}
                    canSignOff={canSignOffBedCount}
                    variant="panel"
                  />
                ) : (
                  <Card>
                    <CardContent className="p-8 text-center text-muted-foreground">
                      No facility assigned for bed count.
                    </CardContent>
                  </Card>
                )}
              </div>}


          {/* Help Section */}
          {activeSection === 'help' && <div className="space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <Button variant="ghost" onClick={() => setActiveSection('census')}>
                    ← Back to Main
                  </Button>
                  <h2 className="text-2xl font-bold">Help & Support</h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* FAQ Section */}
                  <div className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <HelpCircle className="h-5 w-5" />
                          Frequently Asked Questions
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {helpTopics.map((topic) => <div key={topic.id} className="border-b pb-3 last:border-b-0">
                            <h4 className="font-medium text-sm mb-2">{topic.question}</h4>
                            <p className="text-xs text-muted-foreground">{topic.answer}</p>
                          </div>)}
                      </CardContent>
                    </Card>

                    {/* Manual & Resources */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <BookOpen className="h-5 w-5" />
                          Documentation
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button variant="outline" className="w-full justify-start">
                          <FileText className="h-4 w-4 mr-2" />
                          See Manual
                        </Button>
                        <Button variant="outline" className="w-full justify-start">
                          <Video className="h-4 w-4 mr-2" />
                          Training Videos
                        </Button>
                        <Button variant="outline" className="w-full justify-start">
                          <Phone className="h-4 w-4 mr-2" />
                          Emergency Contacts
                        </Button>
                      </CardContent>
                    </Card>
                  </div>

                  {/* AI Assistant & Suggestions */}
                  <div className="space-y-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Bot className="h-5 w-5" />
                          Operations Assistant
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {/* Suggestions */}
                        <div className="mb-4">
                          <h4 className="font-medium text-sm mb-3">Quick Suggestions:</h4>
                          <div className="grid grid-cols-1 gap-2">
                            <Button variant="ghost" size="sm" className="justify-start text-xs h-8" onClick={() => setChatInput("Show me today's check-ins")}>
                              "Show me today's check-ins"
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start text-xs h-8" onClick={() => setChatInput("Find client in room 15")}>
                              "Find client in room 15"
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start text-xs h-8" onClick={() => setChatInput("What's the capacity today?")}>
                              "What's the capacity today?"
                            </Button>
                          </div>
                        </div>

                        {/* Compact Chat Interface */}
                        <div className="space-y-3">
                          <div className="flex gap-2">
                            <Input placeholder="Ask a question..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleChatSubmit()} className="flex-1 h-8 text-sm" />
                            <Button onClick={handleChatSubmit} disabled={!chatInput.trim()} size="sm">
                              <Send className="h-3 w-3" />
                            </Button>
                          </div>
                          
                          {/* Chat Messages - Compact */}
                          {chatMessages.length > 0 && <div className="max-h-48 overflow-y-auto space-y-2 border rounded p-2 bg-muted/20">
                              {chatMessages.slice(-3).map((message) => <div key={message.id} className="text-xs">
                                  <div className={`${message.type === 'user' ? 'text-primary font-medium' : 'text-foreground'}`}>
                                    {message.type === 'user' ? 'You: ' : 'Assistant: '}{message.content}
                                  </div>
                                </div>)}
                            </div>}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>}

          {/* Dashboard Overview */}
          {activeSection === 'dashboard' && <div className="space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-primary/10 rounded-lg">
                          <Users className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Total Clients</p>
                          <p className="text-2xl font-bold">{clients.length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-success/10 rounded-lg">
                          <UserCheck className="h-6 w-6 text-success" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Checked In</p>
                          <p className="text-2xl font-bold">{checkedInCount}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-warning/10 rounded-lg">
                          <AlertTriangle className="h-6 w-6 text-warning" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Active Alerts</p>
                          <p className="text-2xl font-bold">{alerts.length}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-500/10 rounded-lg">
                          <BarChart className="h-6 w-6 text-blue-500" />
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Capacity</p>
                          <p className="text-2xl font-bold">{totalClients > 0 ? Math.round(checkedInCount / totalClients * 100) : 0}%</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Recent Clients */}
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Check-ins</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {clients.filter((c) => c.status === 'checked-in').slice(0, 5).map((client) => <div key={client.id} className="flex items-center gap-3 p-3 hover:bg-muted rounded-lg">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={getClientPhoto(client.id)} alt={client.name} />
                            <AvatarFallback className="bg-muted"><Users className="h-1/2 w-1/2 text-muted-foreground" /></AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium cursor-pointer hover:text-primary" onClick={() => handleSelectClient(client)}>{client.name}</p>
                            <p className="text-sm text-muted-foreground">Room {client.room}</p>
                          </div>
                          <Badge className={getStatusColor(client.status)}>
                            {getStatusIcon(client.status)}
                            <span className="ml-1">Checked In</span>
                          </Badge>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </div>}

          {/* Client Management */}
          {activeSection === 'clients' && <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Client Directory</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {clients.map((client) => <div key={client.id} className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted cursor-pointer" onClick={() => handleSelectClient(client)}>
                          <Avatar className="w-12 h-12">
                            <AvatarImage src={getClientPhoto(client.id)} alt={client.name} />
                            <AvatarFallback className="bg-muted"><Users className="h-1/2 w-1/2 text-muted-foreground" /></AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium cursor-pointer hover:text-primary" onClick={() => handleSelectClient(client)}>{client.name}</p>
                            <p className="text-sm text-muted-foreground">Room {client.room} • {client.caseWorker}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(client.status)}>
                              {getStatusIcon(client.status)}
                              <span className="ml-1 capitalize">{client.status.replace('-', ' ')}</span>
                            </Badge>
                            <Button size="sm" variant="outline" onClick={(e) => {
                      e.stopPropagation();
                      client.status === 'checked-in' ? handleCheckOut(client.id) : handleCheckIn(client.id);
                    }}>
                               {client.status === 'checked-in' ? 'Check Out' : 'Check In'}
                            </Button>
                          </div>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </div>}

          {/* Updates */}
          {activeSection === 'alerts' && <div className="space-y-6">
                <Button variant="ghost" onClick={() => setActiveSection('census')} className="mb-2">
                  <Home className="h-4 w-4 mr-2" />
                  Back to Home
                </Button>
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle>All Alerts</CardTitle>
                      <div className="flex items-center gap-2">
                        <Select value={alertFilter} onValueChange={(v: any) => setAlertFilter(v)}>
                          <SelectTrigger className="w-[130px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="client">Client</SelectItem>
                            <SelectItem value="staff">​</SelectItem>
                            <SelectItem value="system">System</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button onClick={() => setActiveSection('add-alert')} size="sm" className="bg-primary hover:bg-primary/90 h-8">
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(() => {
                  // Build unified alert list
                  type UnifiedAlert = {
                    id: string;
                    title: string;
                    description: string;
                    timestamp: string;
                    category: 'client' | 'staff' | 'system' | 'service' | 'building';
                    type: string;
                    clientName?: string;
                    clientRoom?: string;
                    clientId?: string;
                    acknowledgements?: any[];
                    isClientAlert?: boolean;
                    originalAlert?: string;
                    originalClient?: any;
                  };

                  const unifiedAlerts: UnifiedAlert[] = [];

                  // Add DB alerts (non-client)
                  alerts.filter((a: any) => !a.clientId && a.status === 'active').forEach((alert) => {
                    unifiedAlerts.push({
                      id: alert.id,
                      title: alert.title,
                      description: alert.description,
                      timestamp: alert.timestamp,
                      category: alert.category as any || 'system',
                      type: alert.type,
                      acknowledgements: alert.acknowledgements
                    });
                  });

                  // Add client alerts from client data
                  clients.filter((c) => c.alerts && c.alerts.length > 0).forEach((client) => {
                    client.alerts?.forEach((alert, idx) => {
                      unifiedAlerts.push({
                        id: `${client.id}-${idx}`,
                        title: alert,
                        description: `${client.name} - Room ${client.room}`,
                        timestamp: client.lastActivity || new Date().toISOString(),
                        category: 'client',
                        type: 'info',
                        clientName: client.name,
                        clientRoom: client.room,
                        clientId: client.id,
                        isClientAlert: true,
                        originalAlert: alert,
                        originalClient: client
                      });
                    });
                  });

                  // Filter
                  const filtered = alertFilter === 'all' ?
                  unifiedAlerts :
                  unifiedAlerts.filter((a) => a.category === alertFilter);

                  // Sort by timestamp desc
                  filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

                  if (filtered.length === 0) {
                    return (
                      <div className="text-center py-6 text-muted-foreground">
                              <AlertTriangle className="h-6 w-6 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">No active alerts</p>
                            </div>);

                  }

                  const colorMap: Record<string, {border: string;bg: string;text: string;icon: string;badge: string;}> = {
                    client: { border: 'border-l-green-500', bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-600', badge: 'bg-green-100 text-green-700 border-green-300' },
                    staff: { border: 'border-l-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700 border-blue-300' },
                    system: { border: 'border-l-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', icon: 'text-orange-600', badge: 'bg-orange-100 text-orange-700 border-orange-300' },
                    service: { border: 'border-l-purple-500', bg: 'bg-purple-50', text: 'text-purple-700', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700 border-purple-300' },
                    building: { border: 'border-l-amber-500', bg: 'bg-amber-50', text: 'text-amber-700', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700 border-amber-300' }
                  };

                  return filtered.map((alert) => {
                    const colors = colorMap[alert.category] || colorMap.system;

                    return (
                      <div key={alert.id} className={`p-2.5 border rounded-md border-l-4 ${colors.border} ${colors.bg} flex items-center justify-between gap-2`}>
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <AlertTriangle className={`h-3.5 w-3.5 flex-shrink-0 ${colors.icon}`} />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className={`font-medium text-sm ${colors.text}`}>{alert.title}</span>
                                    {alert.isClientAlert &&
                              <span className={`text-xs ${colors.text}`}>— {alert.clientName} · Room {alert.clientRoom}</span>
                              }
                                  </div>
                                  <p className="text-xs text-muted-foreground">
                                    {alert.timestamp && !isNaN(new Date(alert.timestamp).getTime()) ?
                              format(new Date(alert.timestamp), 'MMM d, yyyy h:mm a') :
                              'No timestamp'}
                                    {!alert.isClientAlert && alert.description && ` · ${alert.description}`}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 capitalize ${colors.badge}`}>
                                  {alert.category}
                                </Badge>
                                {alert.isClientAlert ?
                          !alert.originalAlert?.toLowerCase().includes('meal') &&
                          !alert.originalAlert?.toLowerCase().includes('juice') &&
                          !alert.originalAlert?.toLowerCase().includes('lunch') &&
                          !alert.originalAlert?.toLowerCase().includes('dinner') &&
                          !alert.originalAlert?.toLowerCase().includes('metro') &&
                          !alert.originalAlert?.toLowerCase().includes('laundry') &&
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={async () => {
                            const client = alert.originalClient;
                            const alertText = alert.originalAlert!;
                            const isReminder = alertText.toLowerCase().includes('reminder') || alertText.toLowerCase().includes('appointment') || alertText.toLowerCase().includes('follow up') || alertText.toLowerCase().includes('ilp');
                            const updatedAlerts = client.alerts?.filter((a: string) => a !== alertText) || [];
                            await updateClient({ ...client, alerts: updatedAlerts, lastActivity: new Date().toISOString() });
                            await supabase.from('alerts').update({ status: 'resolved', acknowledged_by: user?.id || null }).eq('client_id', client.id).eq('title', alertText).eq('status', 'active');
                            if (isReminder) {
                              await logActivity({ actionType: 'acknowledge_reminder', actionDetails: { client_id: client.id, client_name: client.name, reminder_text: alertText, timestamp: new Date().toISOString() }, siteId: client.site_id });
                              toast({ title: "Reminder Resolved", description: `Alert resolved and logged` });
                            } else {
                              toast({ title: "Alert Resolved", description: `Alert cleared for ${client.name}` });
                            }
                          }}>
                                      Resolve
                                    </Button> :


                          alert.category === 'staff' &&
                          <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={() => updateAlert({ id: alert.id, status: 'resolved' })}>
                                      Resolve
                                    </Button>

                          }
                              </div>
                            </div>);

                  });
                })()}
                    </div>
                  </CardContent>
                </Card>
              </div>}

          {/* Settings */}
          {activeSection === 'settings' && <div className="space-y-6">
                <div className="flex items-center gap-4 mb-4">
                  <Button variant="ghost" onClick={() => setActiveSection('census')}>
                    <Home className="h-4 w-4 mr-2" />
                    Back to Home
                  </Button>
                </div>
                <Card>
                  <CardHeader>
                    <CardTitle>Settings</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-4 border rounded-lg hover:bg-accent cursor-pointer" onClick={() => setActiveSection('kiosk')}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium mb-2 flex items-center gap-2">
                              <Search className="h-4 w-4" />
                              Quick Check-In View (iPad)
                            </h4>
                            <p className="text-sm text-muted-foreground">Open streamlined kiosk view for fast client check-in/out and service logging.</p>
                          </div>
                          <Button variant="outline" size="sm">
                            Open
                          </Button>
                        </div>
                      </div>
                      <div className="p-4 border rounded-lg opacity-50">
                        <h4 className="font-medium mb-2">Notifications</h4>
                        <p className="text-sm text-muted-foreground">Configure alert preferences and notification settings.</p>
                        <p className="text-xs text-muted-foreground mt-2 italic">Coming soon</p>
                      </div>
                      <div className="p-4 border rounded-lg opacity-50">
                        
                        <p className="text-sm text-muted-foreground">Manage staff accounts and permissions.</p>
                        <p className="text-xs text-muted-foreground mt-2 italic">Available to managers and admins only</p>
                      </div>
                      



                
                    </div>
                  </CardContent>
                </Card>
              </div>}


          {/* Quick Check-In (Kiosk) View - Inline */}
          {activeSection === 'kiosk' && <div className="space-y-4">
              <div className="flex items-center gap-4 mb-2">
                <Button variant="ghost" size="sm" onClick={() => setActiveSection('settings')}>
                  ← Back to Settings
                </Button>
                <h2 className="text-xl font-semibold text-foreground">Quick Check-In View</h2>
              </div>
              
              <div className="space-y-4">
                
                
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="w-[35%] text-sm font-semibold uppercase tracking-wider text-muted-foreground">Client</TableHead>
                        <TableHead className="w-[8%] text-sm font-semibold uppercase tracking-wider text-muted-foreground">Room</TableHead>
                        <TableHead className="w-[12%] text-sm font-semibold uppercase tracking-wider text-muted-foreground">Status</TableHead>
                        <TableHead className="w-[25%] text-sm font-semibold uppercase tracking-wider text-muted-foreground">Alerts</TableHead>
                        <TableHead className="w-[20%] text-sm font-semibold uppercase tracking-wider text-muted-foreground text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getSortedClients().
                  filter((client) => client.status !== 'unbedded' && client.room_id).
                  sort((a, b) => (a.room || '').localeCompare(b.room || '', undefined, { numeric: true })).
                  map((client) =>
                  <TableRow key={client.id} className="hover:bg-muted/30">
                          <TableCell className="py-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-9 w-9">
                                <AvatarImage src={getClientPhoto(client.id)} alt={client.name} />
                                <AvatarFallback className="bg-muted text-xs"><Users className="h-3.5 w-3.5 text-muted-foreground" /></AvatarFallback>
                              </Avatar>
                              <button type="button" className="font-medium text-base text-foreground hover:underline underline-offset-2 cursor-pointer focus:outline-none" onClick={() => handleSelectClient(client)}>{client.name}</button>
                            </div>
                          </TableCell>
                          <TableCell className="py-4">
                            <span className="text-base font-mono text-muted-foreground">{client.room || '—'}</span>
                          </TableCell>
                          <TableCell className="py-4">
                            {isClientOnPass(client) ?
                      <Badge variant="outline" className="text-sm font-medium border-purple-300 text-purple-700 bg-purple-50">
                                Pass {client.passEnd ? `(until ${format(new Date(client.passEnd), 'MMM d')})` : ''}
                              </Badge> :

                      <Badge variant="outline" className={cn("text-sm font-medium capitalize",
                      client.status === 'checked-in' && 'border-[hsl(var(--status-checked-in))]/30 text-[hsl(var(--status-checked-in))] bg-[hsl(var(--status-checked-in))]/10',
                      client.status === 'checked-out' && 'border-[hsl(var(--status-checked-out))]/30 text-[hsl(var(--status-checked-out))] bg-[hsl(var(--status-checked-out))]/10',
                      client.status === 'not_present' && 'border-[hsl(var(--status-not-present))]/30 text-[hsl(var(--status-not-present))] bg-[hsl(var(--status-not-present))]/10',
                      client.status === 'reserved' && 'border-[hsl(var(--status-reserved))]/30 text-[hsl(var(--status-reserved))] bg-[hsl(var(--status-reserved))]/10'
                      )}>
                                {client.status.replace(/[-_]/g, ' ')}
                              </Badge>
                      }
                          </TableCell>
                          <TableCell className="py-4">
                            {client.alerts && client.alerts.length > 0 ?
                      <div className="flex flex-wrap gap-1">
                                {client.alerts.map((alert, idx) =>
                        <Badge key={idx} variant="outline" className="text-xs text-muted-foreground border-border bg-muted/40">
                                    {alert}
                                  </Badge>
                        )}
                              </div> :

                      <span className="text-sm text-muted-foreground">—</span>
                      }
                          </TableCell>
                          <TableCell className="py-4 border-l border-border/50">
                            <div className="flex items-center justify-end gap-1.5">
                               {/* Check In/Out */}
                              {normalizeStatus(client.status) !== 'checked-in' ?
                        <Button size="sm" variant="outline" className="h-9 min-w-[100px] text-sm border-[hsl(var(--status-checked-in))]/40 text-[hsl(var(--status-checked-in))] hover:bg-[hsl(var(--status-checked-in))]/10" onClick={() => handleCheckIn(client.id)}>
                                  Check In
                                </Button> :

                        <Button size="sm" variant="outline" className="h-9 min-w-[100px] text-sm border-[hsl(var(--status-checked-out))]/40 text-[hsl(var(--status-checked-out))] hover:bg-[hsl(var(--status-checked-out))]/10" onClick={() => handleCheckOut(client.id)}>
                                  Check Out
                                </Button>
                        }
                               {/* Services */}
                               <Button size="sm" variant="ghost" className="h-9 w-9 p-0" onClick={() => handleViewServices(client)} title="Services">
                                 <ClipboardPlus className="h-[18px] w-[18px] text-primary" />
                               </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                  )}
                      {getSortedClients().filter((c) => c.status !== 'unbedded' && c.room_id).length === 0 &&
                  <TableRow>
                          <TableCell colSpan={5} className="text-center text-base text-muted-foreground py-8">
                            No bedded clients found
                          </TableCell>
                        </TableRow>
                  }
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>}


          {/* User Guide Section */}
          {activeSection === 'user-guide' && <div className="space-y-4">
                <div className="flex items-center gap-4 mb-4">
                 <Button variant="ghost" onClick={() => setActiveSection('census')}>
                   <Home className="h-4 w-4 mr-2" />
                   Back to Home
                 </Button>
                </div>
                <UserGuide />
              </div>}


          {/* Add Alert Section */}
          {activeSection === 'add-alert' && <div className="space-y-6">
                <div className="flex items-center gap-4 mb-6">
                  <Button variant="ghost" onClick={() => setActiveSection('alerts')}>
                    ← Back to Alerts
                  </Button>
                  <h2 className="text-2xl font-bold">Add New Alert</h2>
                </div>
                
                <Card>
                  <CardHeader>
                    <CardTitle>Create Alert</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Alert Priority</label>
                      <div className="flex gap-2 mt-1">
                        <Button variant={newAlertType === 'info' ? 'default' : 'outline'} size="sm" onClick={() => setNewAlertType('info')}>
                          Low
                        </Button>
                        <Button variant={newAlertType === 'warning' ? 'default' : 'outline'} size="sm" onClick={() => setNewAlertType('warning')}>
                          Medium
                        </Button>
                        <Button variant={newAlertType === 'critical' ? 'default' : 'outline'} size="sm" onClick={() => setNewAlertType('critical')}>
                          High
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Alert Type</label>
                      <div className="flex gap-2 mt-1">
                        <Button variant={newAlertCategory === 'staff' ? 'default' : 'outline'} size="sm" onClick={() => setNewAlertCategory('staff')}>
                          Staff
                        </Button>
                        <Button variant={newAlertCategory === 'service' ? 'default' : 'outline'} size="sm" onClick={() => setNewAlertCategory('service')}>
                          Service
                        </Button>
                        <Button variant={newAlertCategory === 'building' ? 'default' : 'outline'} size="sm" onClick={() => setNewAlertCategory('building')}>
                          Building
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Title</label>
                      <Input value={newAlertTitle} onChange={(e) => setNewAlertTitle(e.target.value)} placeholder="Enter alert title" className="mt-1" />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Description</label>
                      <Textarea value={newAlertDescription} onChange={(e) => setNewAlertDescription(e.target.value)} placeholder="Enter alert description" className="mt-1" rows={3} />
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Client (Optional)</label>
                      <select value={selectedClientForAlert} onChange={(e) => setSelectedClientForAlert(e.target.value)} className="w-full mt-1 p-2 border rounded-md">
                        <option value="">Select a client (optional)</option>
                        {clients.map((client) => <option key={client.id} value={client.id}>
                            {client.name} - Room {client.room}
                          </option>)}
                      </select>
                    </div>
                    
                    <div className="flex gap-2 pt-4">
                      <Button variant="outline" onClick={() => setActiveSection('alerts')}>
                        Cancel
                      </Button>
                      <Button onClick={() => {
                  createAlert({
                    type: newAlertType,
                    title: newAlertTitle,
                    description: newAlertDescription,
                    category: newAlertCategory,
                    clientId: selectedClientForAlert || undefined
                  });
                  setNewAlertTitle('');
                  setNewAlertDescription('');
                  setNewAlertType('info');
                  setNewAlertCategory('staff');
                  setSelectedClientForAlert('');
                  setActiveSection('alerts');
                }} disabled={!newAlertTitle.trim() || !newAlertDescription.trim()} className="bg-primary hover:bg-primary/90">
                        Create Alert
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>}
        </div>
      </main>

      {/* Modals */}
      <Dialog open={isNewClientFormOpen} onOpenChange={setIsNewClientFormOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Client Intake</DialogTitle>
          </DialogHeader>
          <NewClientForm isOpen={isNewClientFormOpen} onClose={() => setIsNewClientFormOpen(false)} onSubmit={(data) => {
          handleNewClient(data);
          setIsNewClientFormOpen(false);
        }} />
        </DialogContent>
      </Dialog>

      <Dialog open={isClientProfileOpen} onOpenChange={setIsClientProfileOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Client Profile</DialogTitle>
          </DialogHeader>
          {selectedProfileClient && <ClientProfile client={selectedProfileClient} isOpen={isClientProfileOpen} onClose={() => setIsClientProfileOpen(false)} getClientPhoto={getClientPhoto} onUpdateClient={handleUpdateClient} onAddServiceRecord={handleAddServiceRecord} availableRooms={availableRooms} onClearAlert={handleClearAlert} />}
        </DialogContent>
      </Dialog>

      {/* Checkout Confirmation Dialog */}
      <Dialog open={isCheckoutDialogOpen} onOpenChange={setIsCheckoutDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm Check Out</DialogTitle>
          </DialogHeader>
          {checkoutClient && <div className="space-y-6">
              <div className="flex items-center gap-6 p-6 border rounded-lg bg-muted/20">
                <Avatar className="w-32 h-32 rounded-lg border-2 border-border">
                  <AvatarImage src={getClientPhoto(checkoutClient.id)} alt={checkoutClient.name} className="object-cover" />
                  <AvatarFallback className="bg-muted rounded-lg">
                    <User className="h-12 w-12 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="font-semibold text-2xl mb-2">{checkoutClient.name}</h3>
                  <p className="text-lg text-muted-foreground mb-1">Room {checkoutClient.room}</p>
                  <p className="text-base text-muted-foreground">Case Manager: {checkoutClient.caseWorker}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Check-in Log</h4>
                <div className="p-4 bg-muted rounded-lg space-y-2 border border-border">
                  {checkoutClient.checkInTime && <p className="text-base">
                      <span className="font-medium">Last Check-in:</span> {formatCheckTime(checkoutClient.checkInTime)}
                    </p>}
                  <p className="text-base">
                    <span className="font-medium">Check-out Time:</span> {new Date().toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Active Alerts */}
              {(() => {
            const clientAlertStrings = checkoutClient.alerts || [];
            const dbClientAlerts = alerts.filter((a) => a.clientId === checkoutClient.id && a.status === 'active');
            const allAlerts: {title: string;description?: string;}[] = [
            ...clientAlertStrings.map((a) => ({ title: a, description: undefined as string | undefined })),
            ...dbClientAlerts.map((a) => ({ title: a.title, description: a.description }))];

            if (allAlerts.length === 0) return null;
            return (
              <div className="space-y-2">
                    <div className="flex items-center gap-2 text-orange-600">
                      <AlertTriangle className="h-4 w-4" />
                      <h4 className="font-semibold text-lg text-orange-600">Active Alerts</h4>
                    </div>
                    <div className="space-y-1.5">
                      {allAlerts.map((alert, idx) =>
                  <div key={idx} className="p-2.5 border border-orange-200 bg-orange-50 rounded-md border-l-4 border-l-orange-400">
                          <p className="text-sm font-medium text-orange-800">{alert.title}</p>
                          {alert.description && <p className="text-xs text-orange-600 mt-0.5">{alert.description}</p>}
                        </div>
                  )}
                    </div>
                  </div>);

          })()}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsCheckoutDialogOpen(false)} size="lg">
                  Cancel
                </Button>
                <Button onClick={confirmCheckOut} size="lg" className="bg-warning hover:bg-warning/80 text-warning-foreground">
                  Confirm Check Out
                </Button>
              </div>
            </div>}
        </DialogContent>
      </Dialog>

      {/* Checkin Confirmation Dialog */}
      <Dialog open={isCheckinDialogOpen} onOpenChange={setIsCheckinDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Confirm Check In</DialogTitle>
          </DialogHeader>
          {checkinClient && <div className="space-y-6">
              <div className="flex items-center gap-6 p-6 border rounded-lg bg-muted/20">
                <Avatar className="w-32 h-32 rounded-lg border-2 border-border">
                  <AvatarImage src={getClientPhoto(checkinClient.id)} alt={checkinClient.name} className="object-cover" />
                  <AvatarFallback className="bg-muted rounded-lg">
                    <User className="h-12 w-12 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h3 className="font-semibold text-2xl mb-2">{checkinClient.name}</h3>
                  <p className="text-lg text-muted-foreground mb-1">Room {checkinClient.room}</p>
                  <p className="text-base text-muted-foreground">Case Manager: {checkinClient.caseWorker}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-lg">Check-in Log</h4>
                <div className="p-4 bg-muted rounded-lg space-y-2 border border-border">
                  {checkinClient.checkOutTime && <p className="text-base">
                      <span className="font-medium">Last Check-out:</span> {formatCheckTime(checkinClient.checkOutTime)}
                    </p>}
                  <p className="text-base">
                    <span className="font-medium">Check-in Time:</span> {new Date().toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Active Alerts */}
              {(() => {
            const clientAlertStrings = checkinClient.alerts || [];
            const dbClientAlerts = alerts.filter((a) => a.clientId === checkinClient.id && a.status === 'active');
            const allAlerts: {title: string;description?: string;}[] = [
            ...clientAlertStrings.map((a) => ({ title: a, description: undefined as string | undefined })),
            ...dbClientAlerts.map((a) => ({ title: a.title, description: a.description }))];

            if (allAlerts.length === 0) return null;
            return (
              <div className="space-y-2">
                    <div className="flex items-center gap-2 text-orange-600">
                      <AlertTriangle className="h-4 w-4" />
                      <h4 className="font-semibold text-lg text-orange-600">Active Alerts</h4>
                    </div>
                    <div className="space-y-1.5">
                      {allAlerts.map((alert, idx) =>
                  <div key={idx} className="p-2.5 border border-orange-200 bg-orange-50 rounded-md border-l-4 border-l-orange-400">
                          <p className="text-sm font-medium text-orange-800">{alert.title}</p>
                          {alert.description && <p className="text-xs text-orange-600 mt-0.5">{alert.description}</p>}
                        </div>
                  )}
                    </div>
                  </div>);

          })()}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setIsCheckinDialogOpen(false)} size="lg">
                  Cancel
                </Button>
                 <Button onClick={confirmCheckIn} size="lg" className="bg-success hover:bg-success/80 text-success-foreground">
                   Confirm Check In
                 </Button>
              </div>
            </div>}
        </DialogContent>
      </Dialog>

      {/* Services Dialog */}
      <Dialog open={isViewServicesOpen} onOpenChange={setIsViewServicesOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={viewServicesClient ? getClientPhoto(viewServicesClient.id) : undefined} />
                <AvatarFallback className="bg-muted"><User className="h-5 w-5 text-muted-foreground" /></AvatarFallback>
              </Avatar>
              <div>
                <span>{viewServicesClient?.name}</span>
                <p className="text-xs font-normal text-muted-foreground font-mono">Room {viewServicesClient?.room || 'N/A'}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          {viewServicesClient && (
            <div className="space-y-5">
              {/* Quick Services */}
               <div>
                <p className="text-sm font-medium mb-2">Quick Services</p>
                {!quickAddService ? (
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Transit', type: 'metro' as const, icon: '🚇' },
                      { label: 'Laundry', type: 'laundry' as const, icon: '👕' },
                      { label: 'Breakfast', type: 'breakfast' as const, icon: '🍳' },
                      { label: 'Lunch', type: 'lunch' as const, icon: '🥗' },
                      { label: 'Dinner', type: 'dinner' as const, icon: '🍽️' },
                      { label: 'Milk', type: 'milk' as const, icon: '🥛' },
                      { label: 'Juice', type: 'juice' as const, icon: '🧃' },
                    ].map((svc) => (
                      <Button
                        key={svc.label}
                        variant="outline"
                        size="sm"
                        className="flex flex-col h-16 gap-1"
                        onClick={() => {
                          setQuickAddService(svc);
                          setQuickAddQuantity(1);
                        }}
                      >
                        <span className="text-lg">{svc.icon}</span>
                        <span className="text-xs">{svc.label}</span>
                      </Button>
                    ))}
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{quickAddService.icon}</span>
                        <span className="font-medium">{quickAddService.label}</span>
                      </div>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setQuickAddService(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="flex items-center justify-center gap-4">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10 w-10 p-0 text-lg font-semibold"
                        onClick={() => setQuickAddQuantity(Math.max(1, quickAddQuantity - 1))}
                        disabled={quickAddQuantity <= 1}
                      >
                        −
                      </Button>
                      <span className="text-2xl font-semibold w-12 text-center tabular-nums">{quickAddQuantity}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10 w-10 p-0 text-lg font-semibold"
                        onClick={() => setQuickAddQuantity(quickAddQuantity + 1)}
                      >
                        +
                      </Button>
                    </div>
                    <Button
                      className="w-full"
                      onClick={async () => {
                        createServiceLog({ clientId: viewServicesClient.id, serviceType: quickAddService.type, quantity: quickAddQuantity });
                        await logActivity({
                          actionType: 'add_service',
                          actionDetails: { client_id: viewServicesClient.id, service_type: quickAddService.type, label: quickAddService.label, quantity: quickAddQuantity }
                        });
                        toast({ title: 'Service Added', description: `${quickAddQuantity}× ${quickAddService.label} added for ${viewServicesClient.name}` });
                        setQuickAddService(null);
                        setQuickAddQuantity(1);
                      }}
                    >
                      Add {quickAddQuantity}× {quickAddService.label}
                    </Button>
                  </div>
                )}
              </div>

              {/* Quick Note */}
              <div>
                <p className="text-sm font-medium mb-2">Quick Note</p>
                <div className="space-y-2">
                  <Input
                    placeholder="Note title"
                    value={quickAddNoteTitle}
                    onChange={(e) => setQuickAddNoteTitle(e.target.value)}
                  />
                  <Textarea
                    placeholder="Note content..."
                    value={quickAddNoteContent}
                    onChange={(e) => setQuickAddNoteContent(e.target.value)}
                    rows={3}
                  />
                  <Button
                    size="sm"
                    disabled={!quickAddNoteTitle.trim() || quickAddSaving}
                    className="w-full"
                    onClick={async () => {
                      if (!quickAddNoteTitle.trim()) return;
                      setQuickAddSaving(true);
                      try {
                        const { data: { user: authUser } } = await supabase.auth.getUser();
                        if (!authUser) throw new Error('Not authenticated');
                        const { error } = await supabase.from('notes').insert({
                          client_id: viewServicesClient.id,
                          site_id: viewServicesClient.site_id || null,
                          title: quickAddNoteTitle,
                          content: quickAddNoteContent || quickAddNoteTitle,
                          created_by: authUser.id
                        });
                        if (error) throw error;
                        await logActivity({
                          actionType: 'create_note',
                          actionDetails: { client_id: viewServicesClient.id, client_name: viewServicesClient.name, note_title: quickAddNoteTitle },
                          siteId: viewServicesClient.site_id
                        });
                        toast({ title: 'Note Saved', description: `Note saved for ${viewServicesClient.name}` });
                        setQuickAddNoteTitle('');
                        setQuickAddNoteContent('');
                      } catch (err: any) {
                        toast({ variant: 'destructive', title: 'Error', description: err.message || 'Failed to save note' });
                      } finally {
                        setQuickAddSaving(false);
                      }
                    }}
                  >
                    <StickyNote className="h-4 w-4 mr-1" />
                    Save Note
                  </Button>
                </div>
              </div>

              {/* Open Full Profile link */}
              <Button
                variant="link"
                className="w-full text-sm"
                onClick={() => {
                  setIsViewServicesOpen(false);
                  handleSelectClient(viewServicesClient);
                }}
              >
                Open Full Profile →
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <MyProfileDialog open={showMyProfile} onOpenChange={setShowMyProfile} />
    </div>;
};
export default StaffDashboard;